import { Stack, ToggleButton } from '@mui/material';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useExtraMembershipLabel } from 'src/modules/member/hooks/useExtraMembershipLabel';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { StyledToggleButtonGroup } from 'src/shared/components/organisms/styledToggleButtonGroup';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';

export interface MembershipTypeItem {
  label: string;
  key: string;
}

export const membershipTypes: MembershipTypeItem[] = [
  {
    label: 'member:memberAdvancedSearch.isMainMember',
    key: 'isMainMember',
  },
  {
    label: 'member:memberAdvancedSearch.isFamilyMember',
    key: 'isFamilyMember',
  },
];

export const MemberAdvancedSearchMembership = () => {
  const { setValue, watch } = useFormContext();
  const { t } = useTranslation(['member']);
  const { currentUser } = useGlobals();

  const useNewFriend = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.UseNewFriend, 'False') === 'True';

  // Kunden kan ha döpt om vänmedlemskapet, och då ska både rubriken och knapparna följa med.
  const friendLabel = useExtraMembershipLabel();

  const [selectedMembershipType, setSelectedMembershipType] = useState<string>('');
  const [selectedExtraType, setSelectedExtraType] = useState<string>('');

  const handleChange = (event: React.MouseEvent<HTMLElement>, newMembershipType: string) => {
    setSelectedMembershipType(newMembershipType);
    setValue('isMainMember', newMembershipType === 'isMainMember' ? 'true' : '');
    setValue('isFamilyMember', newMembershipType === 'isFamilyMember' ? 'true' : '');
  };

  // "Har vänmedlemskap" gäller det ordinarie medlemskapet, så vänposterna själva ska bort ur
  // träfflistan. Därför sätts vänmedlemskap till false samtidigt.
  const extraMemberValue = (newExtraType: string) => {
    if (newExtraType === 'isExtraMember') return 'true';

    return newExtraType === 'hasExtraMemberships' ? 'false' : '';
  };

  const handleExtraChange = (event: React.MouseEvent<HTMLElement>, newExtraType: string) => {
    setSelectedExtraType(newExtraType);
    setValue('isExtraMember', extraMemberValue(newExtraType));
    setValue('hasExtraMemberships', newExtraType === 'hasExtraMemberships' ? 'true' : '');
  };

  const isMainMember = watch('isMainMember');
  const isFamilyMember = watch('isFamilyMember');
  const isExtraMember = watch('isExtraMember');
  const hasExtraMemberships = watch('hasExtraMemberships');

  React.useEffect(() => {
    if (isMainMember) setSelectedMembershipType('isMainMember');
    if (isFamilyMember) setSelectedMembershipType('isFamilyMember');
    if (!isMainMember && !isFamilyMember) setSelectedMembershipType('');
  }, [isMainMember, isFamilyMember]);

  React.useEffect(() => {
    if (hasExtraMemberships) setSelectedExtraType('hasExtraMemberships');
    else if (isExtraMember === 'true') setSelectedExtraType('isExtraMember');
    else setSelectedExtraType('');
  }, [isExtraMember, hasExtraMemberships]);

  return (
    <Stack spacing={2}>
      <FormFieldset legend={t('member:memberAdvancedSearch.membershipType')} inflowLegend noPadding>
        <StyledToggleButtonGroup value={selectedMembershipType} onChange={handleChange} exclusive color='primary' size='small'>
          {membershipTypes.map((x) => (
            <ToggleButton value={x.key} key={x.key}>
              {t(x.label)}
            </ToggleButton>
          ))}
        </StyledToggleButtonGroup>
      </FormFieldset>
      {useNewFriend && (
        <FormFieldset legend={friendLabel} inflowLegend noPadding>
          <StyledToggleButtonGroup value={selectedExtraType} onChange={handleExtraChange} exclusive color='primary' size='small'>
            <ToggleButton value='isExtraMember'>{t('member:memberAdvancedSearch.isExtraMember', { label: friendLabel.toLowerCase() })}</ToggleButton>
            <ToggleButton value='hasExtraMemberships'>
              {t('member:memberAdvancedSearch.hasExtraMemberships', { label: friendLabel.toLowerCase() })}
            </ToggleButton>
          </StyledToggleButtonGroup>
        </FormFieldset>
      )}
    </Stack>
  );
};
